// src/pages/auth/LoginPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../context/AuthContext';
import { useWindow } from '../../context/WindowContext';
import { authService } from '../../services/auth.service';
import { LoginResponse, LoginForm } from '../../types/auth.types';
import { toast } from 'react-toastify';
import logonegro from '../../assets/images/logonegro.svg';
import fondoPantalla from '../../assets/images/fondo_pantalla.svg';
import '../../assets/styles/auth-common.css';
import Footer from '../../components/layout/footer';
import type { AppDispatch, RootState } from '../../store';
import { onboardingAPI } from '../../services/api/onboarding.service';
import { getOnboardingStatus, UpdateUser } from '../../store/authReducer';
import { updateLanguageFromUser } from '../../i18n';
import { getCurrentApiUrl } from '../../config/server.config';

const LoginPage: React.FC = () => {
  const token = authService.getStoredToken();
  const navigate = useNavigate();
  const { login } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const { windowWidth } = useWindow();
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: '',
    remember: false
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState<'EN' | 'ES'>('EN');
  const [showPassword, setShowPassword] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [tempLoginData, setTempLoginData] = useState<{email: string, password: string, remember: boolean} | null>(null);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (error) setError(null);
  };

  const handleDirectRedirect = async (loginResponse: LoginResponse) => {
    try {
        const pathParts = window.location.pathname.split('/');
        const referralCode = pathParts.find(part => part.match(/^[A-Z0-9]{6,}$/));

        if (!loginResponse.user.isEmailVerified) {
          navigate('/verify-email');
          return;
        }

        // 🔍 STEP 1: Check if user has CORPORATE onboarding (check this FIRST before SUBUTAI)
        try {
          const token = localStorage.getItem('token');
          const corporateStatusResponse = await fetch(`${getCurrentApiUrl()}/onboarding/corporate/status`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (corporateStatusResponse.ok) {
            const corporateData = await corporateStatusResponse.json();
            if (corporateData.success && corporateData.data.accountType === 'CORPORATE') {
              console.log('🏢 Corporate onboarding detected:', corporateData.data);

              if (!corporateData.data.isComplete) {
                // Has CORPORATE onboarding in progress → redirect to web form
                console.log('→ Redirecting to corporate web form...');
                navigate("/onboarding/corporate");
                return;
              } else {
                // CORPORATE onboarding complete → update Redux and go to dashboard
                console.log('→ Corporate onboarding complete, updating Redux and going to dashboard');

                // 🔥 CRITICAL: Update Redux to mark onboarding as complete
                dispatch(UpdateUser({
                  ...loginResponse.user,
                  onboardingComplete: true,
                  accountStatus: 'corporate_complete'  // FIX: Set accountStatus properly
                }));

                navigate('/dashboard', { replace: true });
                return;
              }
            }
          }
        } catch (corporateError) {
          // If corporate check fails (404 or error), user is not CORPORATE
          // Continue to check SUBUTAI (EXPRESS/INDIVIDUAL)
          console.log('ℹ️ Not a corporate user, checking EXPRESS/INDIVIDUAL...');
        }

        // 🔍 STEP 2: Check EXPRESS/INDIVIDUAL onboarding (SUBUTAI)
        // IMPORTANT: Only check SUBUTAI onboarding for EXPRESS and INDIVIDUAL users
        // CORPORATE users use the web form, not SUBUTAI
        if (loginResponse.user.accountType !== 'CORPORATE') {
          const response = await dispatch(getOnboardingStatus(referralCode)).unwrap();

          if (!response.data.isComplete) {
            // For EXPRESS and INDIVIDUAL, use SUBUTAI
            console.log('📝 EXPRESS/INDIVIDUAL onboarding incomplete, redirecting to SUBUTAI...');
            navigate("/onboarding");
            return;
          }
        }

        // All onboarding complete
        navigate('/dashboard');
    } catch (error) {
      console.error('Error during redirect:', error);
      toast.error('Error during redirect. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    
    setError(null);
    setLoading(true);

    try {
      if (!formData.email || !formData.password) {
        throw new Error('Please enter both email and password');
      }

      const loginResponse = await login(formData.email, formData.password, formData.remember);
      
      // Check if 2FA is required
      if (loginResponse.requiresTwoFactor) {
        // Store login data temporarily
        setTempLoginData({
          email: formData.email,
          password: formData.password,
          remember: formData.remember
        });
        setShow2FAModal(true);
        setLoading(false);
        return;
      }

      dispatch(UpdateUser(loginResponse.user))
      console.log('Login successful:', loginResponse);
      localStorage.setItem('token', loginResponse.token);
      localStorage.setItem('user', JSON.stringify(loginResponse.user));
      localStorage.setItem('newsView', "false");
      // Update language based on user preference
      updateLanguageFromUser();
      await handleDirectRedirect(loginResponse);
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
      toast.error(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    if (!tempLoginData) return;

    setLoading(true);
    try {
      const loginResponse = await login(
        tempLoginData.email, 
        tempLoginData.password, 
        tempLoginData.remember, 
        twoFactorCode
      );

      if (loginResponse.token) {
        dispatch(UpdateUser(loginResponse.user));
        console.log('Login successful with 2FA:', loginResponse);
        localStorage.setItem('token', loginResponse.token);
        localStorage.setItem('user', JSON.stringify(loginResponse.user));
        localStorage.setItem('newsView', "false");
        setShow2FAModal(false);
        await handleDirectRedirect(loginResponse);
      }
      updateLanguageFromUser();
    } catch (err: any) {
      toast.error('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(()=>{
    const redirectDashboard=()=>{
      if (token) {
          navigate('/dashboard');
      }
    }
    redirectDashboard();
  },[])
  const rootStyle = {
    '--bg-image': `url(${fondoPantalla})`
  } as React.CSSProperties;

  return (
    <div style={{ backgroundColor: '#003DA5', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '30px', width: '100%' }}>
        {/* Logo centrado y más grande - alineado con la tarjeta */}
        <div style={{ textAlign: 'center', width: '400px', maxWidth: '90vw' }}>
          <img src={logonegro} alt="Kanz Bank" style={{ width: 'auto', height: '252px', maxWidth: '100%' }} />
        </div>

        {/* Formulario centrado */}
        <form className="auth-form-container" onSubmit={handleSubmit} style={{ width: '400px', maxWidth: '90vw', position: 'relative', zIndex: 10, margin: '0', gridColumn: 'unset' }}>
          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

          <div className="input-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              disabled={loading}
              required
            />
          </div>

          <div className="input-group">
            <label htmlFor="password">Password</label>
            <div className="password-input">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                disabled={loading}
                required
                style={{width:"80%"}}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
              >
                {showPassword ? 'Hide' : 'View'}
              </button>
            </div>
          </div>

          <div className="remember-forgot">
            <div className="remember-me">
              <input
                type="checkbox"
                id="remember"
                name="remember"
                checked={formData.remember}
                onChange={handleInputChange}
                disabled={loading}
              />
              <label htmlFor="remember">Remember me</label>
            </div>
            <button 
              type="button" 
              onClick={() => navigate('/forgot-password')} 
              className="forgot-password"
              style={{ background: 'none', border: 'none' }}
            >
              Forgot my password?
            </button>
          </div>

          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Open private banking'}
          </button>

          <div className="sm:secondary-buttons flex">
            <button
              type="button"
              className="btn-signup"
              onClick={() => navigate('/signup')}
              disabled={loading}
            >
              Sign up
            </button>
            <button
              type="button"
              className="btn-validate"
              onClick={() => navigate('/verify-email')}
              disabled={loading}
            >
              Validate email
            </button>
          </div>
        </form>
      </div>

      {/* 2FA Verification Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4 text-gray-800">Two-Factor Authentication</h3>

            <p className="text-gray-600 mb-4">
              Enter the 6-digit code from your authenticator app
            </p>

            <div className="mb-4">
              <input
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] text-center text-2xl font-mono tracking-widest"
                maxLength={6}
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShow2FAModal(false);
                  setTwoFactorCode('');
                  setTempLoginData(null);
                }}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handle2FASubmit}
                disabled={loading || twoFactorCode.length !== 6}
                className="flex-1 px-4 py-3 bg-[#B3A369] text-white rounded-lg hover:bg-[#9d8f5c] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>

            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500">
                Lost your authenticator? Use a backup code instead.
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default LoginPage;